import React, { useState, useEffect } from 'react';
import { reportsAPI } from '../../services/garageApi';
import { formatCurrency } from '../../utils/helpers';
import BackButton from '../common/BackButton';

const SupplierBalanceReport = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSupplierBalance = async () => {
            try {
                const response = await reportsAPI.supplierBalance();
                setSuppliers(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Error fetching supplier balances:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSupplierBalance();
    }, []);

    const totals = suppliers.reduce((acc, row) => ({
        purchases: acc.purchases + parseFloat(row.total_purchases || 0),
        paid: acc.paid + parseFloat(row.total_paid || 0),
        balance: acc.balance + parseFloat(row.balance || 0),
    }), { purchases: 0, paid: 0, balance: 0 });

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading report...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Report Header */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <BackButton />
                        <h2 className="text-2xl font-bold text-gray-900">Supplier Balance Report</h2>
                    </div>
                    <div className="text-sm text-gray-500">
                        Generated: {new Date().toLocaleDateString()}
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Total Purchased</p>
                    <p className="text-3xl font-bold text-gray-900">{formatCurrency(totals.purchases)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Total Paid</p>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(totals.paid)}</p>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                    <p className="text-sm font-medium text-gray-600">Outstanding Balance</p>
                    <p className="text-3xl font-bold text-red-600">{formatCurrency(totals.balance)}</p>
                </div>
            </div>

            {/* Balance Table */}
            <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4">Supplier Breakdown</h3>
                {suppliers.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No supplier balance data available.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Purchases</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Paid</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {suppliers.map((supplier) => (
                                <tr key={supplier.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {supplier.name || supplier.company_name || '-'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        {formatCurrency(supplier.total_purchases)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                        {formatCurrency(supplier.total_paid)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                                        {formatCurrency(supplier.balance)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {parseFloat(supplier.balance) <= 0 ? (
                                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                Settled
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                                                Outstanding
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Print Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => window.print()}
                    className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                >
                    Print Report
                </button>
            </div>
        </div>
    );
};

export default SupplierBalanceReport;
